import React from 'react';
import { Fields, reduxForm } from 'redux-form';
import PropTypes from 'prop-types';
import { Header } from 'semantic-ui-react';
import { renderRadioGroup } from '../FormFields';
import { str2bool, scrollOnSubmitError } from '../../utils/utils';
import { validateTermsAndConditions } from '../../utils/validation';
import TermsAndConditionsModal from '../Modals/TermsAndConditionsModal';
import NotEligible from '../NotEligible';

const PharmaTermsAndConditionsForm = props => {
  const { handleSubmit, onSubmit, submitFailed, invalid } = props;
  const sanitizeForm = (values) => {
    const finalForm = Object.assign({}, ...Object.keys(values).map(k => ({ [k]: str2bool(values[k]) })));
    onSubmit({ values: { drugAnswers: finalForm }, path: `/pharmacist/${props.formType}/patient-information` });
  };

  const submitBtnHandler = () => scrollOnSubmitError(invalid);

  return (
    <div className="patient-information form">
      <p className="required">Required field.</p>
      <form className="ui form no-margin" onSubmit={ handleSubmit(sanitizeForm) }>
        <p><b>Please answer the questions below to determine your patient’s eligibility:</b></p>
        <Header as="h2" className="short-margin">
          <Header.Subheader className="short-margin required">
            Does your patient have Medicare?
          </Header.Subheader>
        </Header>
        <Fields
          names={ ['hasMedicare'] }
          component={ renderRadioGroup }
          errorMessage="This is a required field."
          radioName="hasMedicare"
        />
        <Header as="h2" className="short-margin">
          <Header.Subheader className="short-margin required">
                Does your patient have Medicaid?
          </Header.Subheader>
        </Header>
        <Fields
          names={ ['hasMedicaid'] }
          component={ renderRadioGroup }
          errorMessage="This is a required field."
          radioName="hasMedicaid"
        />
        <Header as="h2" className="short-margin">
          <Header.Subheader className="short-margin required">
                Does your patient have any other government program (ie, TRICARE, VA, or DoD, or any other state or government program)?
          </Header.Subheader>
        </Header>
        <Fields
          names={ ['isGovermentPaid'] }
          component={ renderRadioGroup }
          errorMessage="This is a required field."
          radioName="isGovermentPaid"
        />
        <Header as="h2" className="short-margin">
          <Header.Subheader className="short-margin required">
                Your patient certifies that they are at least 18 years old and a resident of the US or a US Territory.
          </Header.Subheader>
        </Header>
        <Fields
          names={ ['isAge18'] }
          component={ renderRadioGroup }
          errorMessage="This is a required field."
          radioName="isAge18"
        />
        <Header as="h2">
          <Header.Subheader className="required">
            Your patient understands that they cannot use this program if their prescription claims for the Takeda Oncology medication are eligible to be reimbursed, in whole or in part, by:
            <ul>
              <li>
                (1) any federal or state government program, including, but not limited to, Medicare, Medicaid, Department of Defense (DoD), Veterans Affairs (VA), TRICARE, or any state or territory pharmaceutical assistance program.
              </li>
              <li>
                (2) any government insurance your patient may become eligible for or start using for a Takeda Oncology medication.
              </li>
              <li>
                (3) commercial (private) prescription insurance or other private health or pharmacy benefit program that covers the entire cost of the medication.
              </li>
            </ul>
          </Header.Subheader>
        </Header>
        <Fields
          names={ ['hasMedicalAcknowledge'] }
          component={ renderRadioGroup }
          errorMessage="This is a required field."
          radioName="hasMedicalAcknowledge"
        />
        <Header as="h2">
          <Header.Subheader className="required">
            Your patient agrees to report their use of this offer to any third party that reimburses them or pays for any part
            of the prescription price. Use of this offer is confirmation that your patient is permitted, under the terms and
            conditions of this health benefit plan(s) covering the prescription, to take advantage of co-pay assistance programs
            that covers their Takeda Oncology medication.
          </Header.Subheader>
        </Header>
        <Fields
          names={ ['isAgree'] }
          component={ renderRadioGroup }
          errorMessage="This is a required field."
          radioName="isAgree"
        />
        <Header as="h2">
          <Header.Subheader className="required">
            Your patient agrees that they will not submit the cost of any portion of the product dispensed pursuant to this Program to a federal or state healthcare program (including, but not limited to Medicare, Medicaid, TRICARE, VA, DoD, etc.), for purposes of counting it toward their out-of-pocket expenses, and to notify Takeda Oncology Here2Assist&reg; if they become eligible for a federal or state healthcare program.
          </Header.Subheader>
        </Header>
        <Fields
          names={ ['hasAnyOtherGovermentInsurance'] }
          component={ renderRadioGroup }
          errorMessage="This is a required field."
          radioName="hasAnyOtherGovermentInsurance"
        />
        <Header as="h2">
          <Header.Subheader>
            If your patient meets all Program eligibility requirements, they could pay as little as Zero Dollars ($0.00)
            of their co-pay amount and Takeda may pay the remaining co-pay or co-insurance for each prescription.
            Your patient will be responsible for any remaining co-pay or co-insurance balance for each prescription.
            This Program is not conditioned on any past, present or future purchase of any Takeda product, including
            refills. This Program is valid for up to 12 months, and your patient’s co-pay card may be renewed every 12 months,
            subject to continued eligibility at the beginning of each calendar year. This offer is not valid with any other program, discount, or offer involving your patient’s prescribed Takeda Oncology medication. This offer may be rescinded, revoked, or amended without
            notice. No reproductions. This offer is void where prohibited by law, taxed, or restricted. Limit one offer per
            purchase. No income requirements or membership fees. This Program is not health insurance. Cash value of 1/100 of 1¢.
            <br/>
            <br/>
          </Header.Subheader>
        </Header>
        <p className="required terms-radio-label">
          Your patient has read and understands the <TermsAndConditionsModal trigger={ <a className={ `track track-78 track-env-${props.environment}` } rel="noopener noreferrer">Terms and Conditions</a> }/> for participation in the Takeda Oncology
          Co-Pay Assistance Program, and certifies that they meet all requirements.
        </p>
        <Fields
          names={ ['termAndCondAccepted'] }
          component={ renderRadioGroup }
          errorMessage="This is a required field."
          radioName="termAndCondAccepted"
        />

        <p className='info-terms'>For questions about this offer, please contact the Takeda Oncology Co-Pay
            Assistance Program, a patient support service of Takeda Oncology Here2Assist,
            at <span className='nowrap-txt'>1-844-817-6468</span>, Option 2, Monday-Friday, 8AM-8PM ET.</p>

        <Fields
          names={ ['isEligible'] }
          component={ (fields) => {
            const showError = fields[fields.radioName] && fields[fields.radioName].meta.error;
            return (<React.Fragment>
              {submitFailed && showError && fields.errorMessage}
            </React.Fragment>);
          } }
          errorMessage={ <NotEligible hcp /> }
          radioName="isEligible"
        />

        <button
          onClick={ submitBtnHandler }
          type="submit"
          className= { `ui button form-button track ${props.submitTrackId} track-env-${props.environment}` }
          disabled={ submitFailed && invalid }
        >
            Next
        </button>

      </form>
    </div>
  );
};

PharmaTermsAndConditionsForm.propTypes = {
  environment: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  submitTrackId: PropTypes.string.isRequired,
};

export default reduxForm({
  form: 'pharmaTermsAndConditionsForm', // a unique identifier for this form
  validate: validateTermsAndConditions, // <--- validation function given to redux-form
})(PharmaTermsAndConditionsForm);
